import { type FC, useMemo, useState } from 'react';

import Search from '@/components/DesignLibrary/Atoms/Search';
import Button from '@/components/DesignLibrary/Button';
import { TBody, TD, TH, THead, Table, TableRow } from '@/components/DesignLibrary/Table';
import Shimmer from '@/components/Shimmer';

import AutofillData from './AutofillData';
import s from './ConnectedDataPanel.module.scss';

type Props = {
  data?: {
    title: string;
    values: { property: string; value: string }[] | null;
    onEditClick?: () => void;
    onClose?: () => void;
    type: 'autofill' | 'ai-autofill' | 'questionnaire' | 'dataset' | null;
    tableData?: Array<Record<string, unknown>>;
  } | null;
  isLoading?: boolean;
};

const ConnectedDataPanel: FC<Props> = ({ data, isLoading = false }) => {
  const type = data?.type || 'questionnaire';
  const [datasetSearch, setDatasetSearch] = useState('');

  const datasetRows = useMemo(() => {
    if (type !== 'dataset' || !data?.tableData) {
      return data?.tableData || [];
    }

    const query = datasetSearch.trim().toLowerCase();
    if (!query) {
      return data.tableData;
    }

    return data.tableData.filter((row) =>
      Object.values(row).some((value) => {
        if (value === null || value === undefined) {
          return false;
        }
        return String(value).toLowerCase().includes(query);
      }),
    );
  }, [type, data?.tableData, datasetSearch]);

  return (
    <aside className={s.panel}>
      {!isLoading ? (
        <>
          {type === 'questionnaire' && (
            <div className={s.card}>
              {data?.title && <p className='body-md-bold'>{data?.title}</p>}
              {data?.onEditClick && (
                <Button variant='secondary' size='xs' onClick={data.onEditClick} className={s.editButton}>
                  Edit
                </Button>
              )}
              <Table>
                <THead>
                  <TableRow>
                    <TH label='Property' />
                    <TH label='Value' />
                  </TableRow>
                </THead>
                <TBody>
                  {data?.values?.map(({ property, value }) => (
                    <TableRow key={property}>
                      <TD compact label={property} />
                      <TD compact label={value} />
                    </TableRow>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
          {(type === 'autofill' || type === 'ai-autofill') && <AutofillData data={data} />}
          {type === 'dataset' && data?.tableData && (
            <div className={s.card}>
              <div className={s.datasetHeader}>
                {data?.title && (
                  <p className='body-md-bold' style={{ margin: 0 }}>
                    {data?.title}
                  </p>
                )}
                <p className='caption' style={{ color: 'var(--color-gray-75)', margin: 0 }}>
                  {datasetSearch.trim()
                    ? `${datasetRows.length} of ${data.tableData.length} records`
                    : `${data.tableData.length} records`}
                </p>
              </div>
              <div className={s.datasetSearch}>
                <Search
                  placeholder={`Search ${data?.title ? data.title.toLowerCase() : 'records'}`}
                  ariaLabel={`Search ${data?.title ? data.title.toLowerCase() : 'records'}`}
                  onChange={setDatasetSearch}
                />
              </div>
              <div className={s.datasetTable}>
                {datasetRows.length > 0 ? (
                  <Table>
                    <THead>
                      <TableRow>
                        {Object.keys(data.tableData[0]).map((tableKey) => (
                          <TH key={tableKey} label={tableKey.charAt(0).toUpperCase() + tableKey.slice(1)} />
                        ))}
                      </TableRow>
                    </THead>
                    <TBody>
                      {datasetRows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {Object.values(row).map((value, colIndex) => (
                            <TD key={colIndex} compact label={String(value || '—')} />
                          ))}
                        </TableRow>
                      ))}
                    </TBody>
                  </Table>
                ) : (
                  <p className='caption' style={{ color: 'var(--color-gray-75)', margin: 'var(--padding-df)' }}>
                    No records match your search.
                  </p>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <Shimmer className={s.shimmer} />
      )}
    </aside>
  );
};

export default ConnectedDataPanel;
